// Runtime SEO para a pagina de detalhe (client-side).
//
// O HTML inicial (index.html) traz OG/Twitter GENERICOS do site. Como estamos
// em Azure SWA Free tier, NAO ha render server-side por experiencia — entao:
//
//   - Google (executa JS): esta funcao injeta o canonical correto (/{slug}) e
//     o JSON-LD Product por experiencia, inclusive em /{experienceCode}, onde
//     o canonical aponta para a URL amigavel (evita conteudo duplicado).
//   - Crawlers sociais (NAO executam JS): veem apenas o OG generico do HTML —
//     comportamento aceito por ora (sem OG dinamico por experiencia).
//
// Idempotente: reaproveita os elementos pelo id (#ld-experience) e pela tag
// <link rel="canonical">, sobrescrevendo o que veio no HTML — nunca duplica.

import { config } from '../config/env';
import type { ExperienceListItem } from '../types';

const LD_ID = 'ld-experience';

// priceCurrency do Schema.org exige ISO 4217 ("BRL"); a API manda "R$".
function toIsoCurrency(raw: string): string {
  const v = (raw || '').trim().toUpperCase();
  return /^[A-Z]{3}$/.test(v) ? v : 'BRL';
}

function canonicalUrlFor(exp: ExperienceListItem): string {
  const path = exp.slug || exp.experienceCode;
  return `${config.siteUrl}/${path}`;
}

function buildProductSchema(exp: ExperienceListItem, url: string) {
  const image =
    exp.images && exp.images.length > 0
      ? exp.images[0]
      : `${config.siteUrl}/img/og-itaqueri.jpg`;

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: exp.title,
    description: (exp.description || exp.summary || exp.title || '').trim().slice(0, 5000),
    image,
    url,
    sku: exp.experienceCode,
    brand: { '@type': 'Brand', name: 'Reserva Facil' },
  };

  if (exp.category) schema.category = exp.category;

  if (exp.price > 0) {
    schema.offers = {
      '@type': 'Offer',
      price: exp.price.toFixed(2),
      priceCurrency: toIsoCurrency(exp.currency),
      availability: 'https://schema.org/InStock',
      url,
    };
  }

  return schema;
}

function upsertCanonical(href: string): void {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = href;
}

function upsertJsonLd(schema: object): void {
  let script = document.getElementById(LD_ID) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = LD_ID;
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(schema);
}

/**
 * Aplica canonical + JSON-LD Product para a experiencia. Chamar num useEffect
 * da pagina de detalhe. Retorna uma funcao de cleanup que remove o JSON-LD
 * injetado em runtime ao desmontar (evita schema de uma experiencia "vazar"
 * para outra rota client-side).
 */
export function applyExperienceSeo(exp: ExperienceListItem): () => void {
  const url = canonicalUrlFor(exp);
  upsertCanonical(url);
  upsertJsonLd(buildProductSchema(exp, url));

  return () => {
    document.getElementById(LD_ID)?.remove();
  };
}
